/**
 * Der Speicher auf dem Gerät (S4.3) als **Schnittstelle** — vier Operationen
 * auf vier Lagern, ohne Indizes, ohne Transaktionen über Lagergrenzen, ohne
 * Migrationen.
 *
 * Die Ausführung gehört der Plattform: IndexedDB im Browser
 * (`src/features/mobile/offline/db.ts`), auf dem Gerät ein anderer Speicher
 * (M2.2). Was hier steht, ist das, worauf sich beide einigen müssen — plus die
 * flüchtige Ausführung, die keine Plattform braucht.
 *
 * **Zwei Ausführungen, dieselbe Schnittstelle.** `createMemoryDb()` ist nicht
 * nur für Tests da: in einer Umgebung mit gesperrtem Speicher läuft die App
 * damit weiter, nur überlebt der Tagesbestand keinen Neustart — deutlich besser
 * als eine Ansicht, die gar nicht erst startet.
 */

/** Die vier Lager. Getrennt, weil sie verschiedene Lebensdauern haben — der Tag ist Wegwerfware, die Warteschlange nicht. */
export type StoreName = "day" | "orders" | "reference" | "outbox";

const STORE_NAMES: StoreName[] = ["day", "orders", "reference", "outbox"];

export interface OfflineDb {
  get<T>(store: StoreName, key: string): Promise<T | null>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
  all<T>(store: StoreName): Promise<T[]>;
  keys(store: StoreName): Promise<string[]>;
}

/** Dieselbe Schnittstelle ohne Datenträger — Rückfallebene ohne IndexedDB und der Speicher, gegen den die Tests laufen. */
export function createMemoryDb(): OfflineDb {
  const lager = new Map<StoreName, Map<string, unknown>>(STORE_NAMES.map((name) => [name, new Map()]));
  const of = (store: StoreName) => lager.get(store)!;
  return {
    get: <T,>(store: StoreName, key: string) => Promise.resolve((of(store).get(key) as T | undefined) ?? null),
    put: (store, key, value) => {
      of(store).set(key, value);
      return Promise.resolve();
    },
    remove: (store, key) => {
      of(store).delete(key);
      return Promise.resolve();
    },
    all: <T,>(store: StoreName) => Promise.resolve([...of(store).values()] as T[]),
    keys: (store) => Promise.resolve([...of(store).keys()])
  };
}

/**
 * Reicht an `primary` durch und wechselt beim **ersten** Fehlschlag dauerhaft auf
 * einen flüchtigen Speicher — für den Rest der Sitzung, ohne es noch einmal zu
 * versuchen.
 *
 * Dauerhaft und nicht je Aufruf, weil ein halb funktionierender Speicher das
 * Schlimmste wäre: ein Eintrag läge in IndexedDB, der nächste im Ersatz, und die
 * Warteschlange hätte zwei Hälften, die nichts voneinander wissen.
 *
 * Der Preis ist Ehrlichkeit gegenüber der Haltbarkeit: was danach entsteht,
 * überlebt keinen Neustart. Die Alternative wäre, dem Fahrer seine Eingabe
 * wortlos wegzunehmen — das ist schlechter.
 */
export function withMemoryFallback(primary: OfflineDb): OfflineDb {
  let ersatz: OfflineDb | null = null;

  const run = async <T>(aufruf: (db: OfflineDb) => Promise<T>): Promise<T> => {
    if (ersatz) return aufruf(ersatz);
    try {
      return await aufruf(primary);
    } catch {
      ersatz = createMemoryDb();
      return aufruf(ersatz);
    }
  };

  return {
    get: (store, key) => run((db) => db.get(store, key)),
    put: (store, key, value) => run((db) => db.put(store, key, value)),
    remove: (store, key) => run((db) => db.remove(store, key)),
    all: (store) => run((db) => db.all(store)),
    keys: (store) => run((db) => db.keys(store))
  };
}
